/**
 * Defensively parse a fetch Response body as JSON.
 *
 * QC-016: calling `response.json()` on an empty or non-JSON body (e.g. an API
 * gateway 502 with an empty payload, or a non-OK response with no body) throws
 * `Unexpected end of JSON input` / `invalid JSON` as an unhandled rejection in
 * the browser console. This helper guards every client-side JSON response
 * parse: it verifies the response status, reads the body as text, skips the
 * parse entirely on empty bodies, and catches parse errors.
 */
export const safeJsonResponse = async <T>(
  response: Response
): Promise<T | null> => {
  if (!response.ok) return null;

  const text = await response.text();

  if (!text || !text.trim()) return null;

  try {
    return JSON.parse(text) as T;
  } catch {
    // Body was non-empty but not valid JSON — treat as a failed response.
    return null;
  }
};
